import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || "/account";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/assets/booking-bg.jpg')" }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-[#0B0B0C]/80 backdrop-blur-sm" aria-hidden />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          data-testid="auth-brand-link"
          className="block text-center mb-10"
        >
          <div className="font-serif text-3xl font-semibold tracking-tight text-white">
            Phantasm
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6]">
            Limousine Service Inc.
          </div>
        </Link>

        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="backdrop-blur-2xl bg-[#121214]/80 border border-white/10 p-10 shadow-2xl"
        >
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-3">
            Account
          </div>
          <h1 className="font-serif text-3xl text-white font-semibold tracking-tight mb-10">
            Sign in
          </h1>

          <div className="space-y-7">
            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                autoComplete="email"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder-[#8E9AA6] focus:border-white focus:outline-none transition-colors font-sans text-base"
                placeholder="you@phantasmlimousineservice.com"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                autoComplete="current-password"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder-[#8E9AA6] focus:border-white focus:outline-none transition-colors font-sans text-base"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mt-6 text-xs text-red-400 tracking-wide"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="w-full mt-10 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-5 text-center">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[11px] uppercase tracking-[0.22em] text-[#8E9AA6] hover:text-white transition-colors">
              Forgot password?
            </Link>
          </div>

          <div className="mt-8 text-center text-xs text-[#8E9AA6] tracking-wide">
            New to Phantasm?{" "}
            <Link
              to="/register"
              data-testid="login-to-register-link"
              className="text-white hover:text-[#E6E6E6] transition-colors uppercase tracking-[0.2em]"
            >
              Create account
            </Link>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            data-testid="login-back-home"
            className="text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6] hover:text-white transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
